<script lang="ts">
  import * as Sentry from "@sentry/browser";
  import { readable } from "svelte/store";
  import { form } from "svelte-forms2";
  import StatesList from "@shared/svelte/StatesList";

  // services
  import { blur, change, selectChange } from "@shared/services/svelte_form_input_service";
  import {
    businessDetailsErrorsMap,
    retailerLiquorLicenseTypes,
    setErrors,
    trackErrorImpressions,
    userFriendlyErrors,
  } from "@onboarding2/services/retailer_user_signup";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { extractGooglePlaceData } from "@onboarding2/services/google_places_autocomplete_service";
  import { RetailerEntity } from "@shared/services/event_tracking/entities";

  // stores
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  // components
  import FormInputError from "@shared/svelte/Components/FormInputError.svelte";
  import FormStepper from "@onboarding2/svelte/Shared/FormStepper.svelte";
  import Header from "@onboarding2/svelte/Shared/Header.svelte";
  import SelectInput from "@shared/svelte/Components/SelectInput.svelte";
  import StepperFooter from "@onboarding2/svelte/Shared/StepperFooter.svelte";
  import TextInput from "@shared/svelte/Components/TextInput.svelte";

  // types
  import type { FormField } from "@shared/types/Form";
  import type { RetailerType } from "@shared/types/Retailer";
  import { onMount } from "svelte";

  export let fields: RetailerManualCompleteSignupFields;
  export let googlePlaceResult = null;
  export let hasSelectedRetailer: boolean = false;
  export let retailer: RetailerType;
  export let retailerStore;

  type RetailerManualCompleteSignupFields = {
    address1: FormField;
    city: FormField;
    licenseType: FormField;
    liquorLicense: FormField;
    liquorLicenseAck: FormField;
    name: FormField;
    nonAlcoholic: FormField;
    state: FormField;
    zip: FormField;
  };

  const { trackClick, trackEvent, immediatelyProcessEventQueue } = EventTracking;

  const businessDetailsForm = form(...Object.values(fields));
  const liquorLicense = businessDetailsForm.getField("liquor_license");
  const liquorLicenseAck = businessDetailsForm.getField("liquor_license_ack");
  const nonAlcoholic = businessDetailsForm.getField("non_alcoholic");
  const params = readable(window["stateParams"]);

  let submitting: boolean = false;

  onMount(() => {
    // hack because the salesforce chat overlaps the footer buttons
    // @ts-ignore
    if (window.embedded_svc) {
      const footer = <HTMLElement>document.querySelector('[data-grid-area="footer"]');
      footer.style.paddingTop = "10px";
      footer.style.paddingBottom = "58px";
    }
  });

  const save = async (
    retailer: RetailerType,
    successCallback: () => Promise<void>,
  ): Promise<void> => {
    await businessDetailsForm.validate();

    if (!$businessDetailsForm.valid) {
      return trackErrorImpressions(
        userFriendlyErrors($businessDetailsForm.errors, businessDetailsErrorsMap),
      );
    }

    submitting = true;

    // We're setting accepted terms to true because they agree by clicking the sign-up button
    Object.assign(retailer, {
      ...$businessDetailsForm.summary,
      accepted_terms: true,
      completed_onboarding: true,
    });

    if (String(retailer.non_alcoholic) == "true") retailer.liquor_license = null;
    if (retailer.liquor_license_ack) retailer.license_terms_ack_by_user_id = $CurrentUserStore.id;

    if (hasSelectedRetailer) {
      retailer.google_place_location_data = googlePlaceResult;
      retailer.address2 = extractGooglePlaceData(googlePlaceResult)["address2"];
    }

    retailerStore
      .save(retailer)
      .then(async res => {
        trackEvent("retailer_created", [
          RetailerEntity.fromRetailer({ ...retailer, id: res.retailer_id }),
        ]);
        await successCallback();
      })
      .catch((error: { errors: { messages: string[]; keys: string[] } }) => {
        if (error.errors.messages[0] == "Authentication required") {
          Sentry.captureMessage(
            "Unauthenticated attempt to create a retailer in the manual signup flow",
          );
          setTimeout(() => window.location.reload(), 500);
        }

        setErrors(retailerStore, error.errors.messages);
        submitting = false;
      });
  };

  const goHome = async (): Promise<void> => {
    await immediatelyProcessEventQueue();
    window.location.reload();
  };

  const goNext = async (): Promise<void> =>
    window["stateService"].go("layout.signedIn.distributors", $params);

  $: shouldAckBeDisabled = !($liquorLicense.value || $nonAlcoholic.value == "true");
  $: if ($liquorLicense.value === "") $liquorLicense.value = null;
</script>

<main class="fizz-layout-1-col-narrow-header-footer">
  <Header title={"Account setup"}>
    <FormStepper currentStep={1} />
  </Header>
  <div data-grid-area="main">
    <h2 class="fizz-heading-3 fizz-stack-8 fizz-text-center-on-large">
      Let's get your business set up!
    </h2>
    <p class="fizz-stack-32 fizz-text-center-on-large fizz-text-medium">
      Tell us about your business so you can start placing orders to a location. You can add more
      locations later.
    </p>
    <form class="fizz-flow-16" data-testid="form">
      <div id="biz-name-container">
        <TextInput
          classes={"fizz-flex-item fizz-full-width"}
          field={fields.name}
          id="name"
          label="Business Name"
          name="name"
          on:blur={blur}
          on:change={change}
          on:click={() => trackClick("onboarding_name_field")}
        >
          {#each Object.entries(businessDetailsErrorsMap.name) as [key, error]}
            <FormInputError form={businessDetailsForm} errorName="name.{key}">
              {error}
            </FormInputError>
          {/each}
        </TextInput>
      </div>
      <div id="address-container">
        <TextInput
          classes={"fizz-flex-item fizz-full-width"}
          field={fields.address1}
          id="address1"
          label="Business Address"
          name="address1"
          on:blur={blur}
          on:change={change}
          on:click={() => trackClick("onboarding_address_1_field")}
        >
          {#each Object.entries(businessDetailsErrorsMap.address1) as [key, error]}
            <FormInputError form={businessDetailsForm} errorName="address1.{key}">
              {error}
            </FormInputError>
          {/each}
        </TextInput>
      </div>
      <div id="city-zip-container" class="fizz-flex-row-8 fizz-align-content-start">
        <div class="fizz-flex-item" id="city-container">
          <TextInput
            classes={"fizz-flex-item fizz-full-width"}
            field={fields.city}
            id="city"
            label="City"
            name="city"
            on:blur={blur}
            on:change={change}
            on:click={() => trackClick("onboarding_city_field")}
          >
            {#each Object.entries(businessDetailsErrorsMap.city) as [key, error]}
              <FormInputError form={businessDetailsForm} errorName="city.{key}">
                {error}
              </FormInputError>
            {/each}
          </TextInput>
        </div>
        <div class="fizz-flex-item" id="zip-container">
          <TextInput
            classes={"fizz-flex-item fizz-full-width"}
            field={fields.zip}
            id="zip"
            label="Zipcode"
            name="zip"
            on:blur={blur}
            on:change={change}
            on:click={() => trackClick("onboarding_address_zip_field")}
          >
            {#each Object.entries(businessDetailsErrorsMap.zip) as [key, error]}
              <FormInputError form={businessDetailsForm} errorName="zip.{key}">
                {error}
              </FormInputError>
            {/each}
          </TextInput>
        </div>
      </div>
      <div id="state-container">
        <SelectInput
          options={{
            list: StatesList,
            textKey: "name",
            valueKey: "code",
          }}
          field={fields.state}
          id="state"
          label="State"
          name="state"
          on:change={e => {
            selectChange(e);
            trackClick("onboarding_state_select");
          }}
        >
          {#each Object.entries(businessDetailsErrorsMap.state) as [key, error]}
            <FormInputError form={businessDetailsForm} errorName="state.{key}">
              {error}
            </FormInputError>
          {/each}
        </SelectInput>
      </div>
      <div id="premise-container">
        <SelectInput
          options={{
            list: retailerLiquorLicenseTypes,
            textKey: "text",
            valueKey: "value",
          }}
          field={fields.licenseType}
          id="license_type"
          label="How are beverages consumed at this location?"
          name="license_type"
          on:change={e => {
            selectChange(e);
            trackClick("onboarding_license_type_field");
          }}
        >
          {#each Object.entries(businessDetailsErrorsMap.license_type) as [key, error]}
            <FormInputError form={businessDetailsForm} errorName="license_type.{key}">
              {error}
            </FormInputError>
          {/each}
        </SelectInput>
      </div>
      <div id="alcohol-container">
        <span class="fizz-label fizz-stack-16">Does this location sell alcoholic beverages?</span>
        <label class="fizz-flex-row-8 fizz-stack-8 fizz-text-medium">
          <input
            class="fizz-radio-button"
            data-testid="non_alcoholic_false"
            id="non_alcoholic_false"
            name="non_alcoholic"
            type="radio"
            value="false"
            on:click={() => trackClick("onboarding_non_alcoholic_radio_true")}
            bind:group={$nonAlcoholic.value}
          />
          <span>Yes</span>
        </label>
        <label class="fizz-flex-row-8 fizz-stack-8 fizz-text-medium" id="alcoholic_no">
          <input
            class="fizz-radio-button"
            data-testid="non_alcoholic_true"
            id="non_alcoholic_true"
            name="non_alcoholic"
            type="radio"
            value="true"
            on:click={() => trackClick("onboarding_non_alcoholic_radio_false")}
            bind:group={$nonAlcoholic.value}
          />
          <span>No</span>
        </label>
      </div>
      {#if $nonAlcoholic.value !== "true"}
        <div class="fizz-stack-4" id="liquor-license-container">
          <TextInput
            classes={"fizz-flex-item fizz-full-width"}
            field={fields.liquorLicense}
            id="liquor_license"
            label="Liquor License"
            name="liquor_license"
            on:blur={blur}
            on:change={change}
            on:click={() => trackClick("onboarding_liquor_license_field")}
          />
        </div>
        <div class="text-subdued">This is required to order alcohol, but not for signing up</div>
      {/if}
      <div class="fizz-stack-4" id="liquor-license-ack-container">
        <label class="fizz-flex-row-8" for="liquor_license_ack">
          <input
            style="min-width: 1rem"
            class="fizz-checkbox"
            class:fizz-input-error={!$liquorLicenseAck.valid}
            disabled={shouldAckBeDisabled}
            id="liquor_license_ack"
            name="liquor_license_ack"
            data-testid="liquor_license_ack"
            type="checkbox"
            on:click={() => trackClick("onboarding_liquor_license_acknowledged")}
            bind:checked={$liquorLicenseAck.value}
          />
          <span class:fizz-subdued={shouldAckBeDisabled}>
            {#if $nonAlcoholic.value == "true"}
              I certify that the account I represent and/or am ordering on behalf of does not sell
              beverage alcohol and is not a holder of a duly issued liquor license
            {:else}
              I certify that the above-referenced liquor license is currently valid and duly issued
              by the appropriate governmental agency
            {/if}
          </span>
        </label>
        {#each Object.entries(businessDetailsErrorsMap.liquor_license_ack) as [key, error]}
          <FormInputError form={businessDetailsForm} errorName="liquor_license_ack.{key}">
            {error}
          </FormInputError>
        {/each}
      </div>
    </form>
  </div>
  <StepperFooter
    disabled={submitting}
    saveFn={() => save(retailer, goHome)}
    nextFn={() => save(retailer, goNext)}
  />
</main>
